import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Plus, Pencil, Trash2, Save, X, Search } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  title: "", occasion: "All", season: "All Seasons",
  emoji: "✨", blurb: "", product_ids: [], sort_order: 0,
};

export default function CombosAdmin({ products = [] }) {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pickSearch, setPickSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/combos");
      setCombos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: combos.length });
    setPickSearch("");
  };

  const startEdit = (c) => {
    setCreating(false);
    setEditingId(c.id);
    setForm({
      title: c.title, occasion: c.occasion, season: c.season,
      emoji: c.emoji, blurb: c.blurb,
      product_ids: [...(c.product_ids || [])],
      sort_order: c.sort_order ?? 0,
    });
    setPickSearch("");
  };

  const cancel = () => { setCreating(false); setEditingId(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, title: form.title.trim() };
      if (creating) {
        await api.post("/admin/combos", payload);
        toast.success("Combo added");
      } else if (editingId) {
        await api.put(`/admin/combos/${editingId}`, payload);
        toast.success("Combo updated");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete combo "${c.title}"?`)) return;
    try {
      await api.delete(`/admin/combos/${c.id}`);
      toast.success("Combo deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePick = (pid) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(pid)
        ? f.product_ids.filter((x) => x !== pid)
        : [...f.product_ids, pid],
    }));
  };

  const productIndex = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  const filteredPickOptions = useMemo(() => {
    const needle = pickSearch.trim().toLowerCase();
    const list = needle
      ? products.filter(
          (p) =>
            p.brand.toLowerCase().includes(needle) ||
            p.name.toLowerCase().includes(needle)
        )
      : products;
    return list.slice(0, 120);
  }, [products, pickSearch]);

  const isFormOpen = creating || editingId;

  return (
    <section className="bg-[var(--bg-surface)] border border-[var(--border-default)] p-6">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[var(--brand-gold)]" />
          <h2 className="font-headings text-xl">Curated Combos</h2>
        </div>
        <button
          data-testid="combo-add-btn"
          onClick={startCreate}
          className="flex items-center gap-1 px-4 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)]"
        >
          <Plus size={14} />
          Add Combo
        </button>
      </div>

      {isFormOpen && (
        <div data-testid="combo-form" className="border border-[var(--brand-gold)]/40 p-5 mb-6 space-y-4">
          <div className="font-headings text-lg">{creating ? "New Combo" : "Edit Combo"}</div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Emoji</label>
              <input data-testid="combo-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} maxLength={4} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-lg" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Title</label>
              <input data-testid="combo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Date Night" className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Occasion</label>
              <input data-testid="combo-occasion" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Season</label>
              <input data-testid="combo-season" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Sort Order</label>
              <input data-testid="combo-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">Blurb (shown below title)</label>
              <textarea data-testid="combo-blurb" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} rows={2} className="mt-1 w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                Picks ({form.product_ids.length} selected)
              </label>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  data-testid="combo-pick-search"
                  value={pickSearch}
                  onChange={(e) => setPickSearch(e.target.value)}
                  placeholder="Search perfumes…"
                  className="bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none pl-6 pr-2 py-1 text-xs w-52"
                />
              </div>
            </div>
            {form.product_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.product_ids.map((pid) => {
                  const p = productIndex[pid];
                  return (
                    <span
                      key={pid}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] border ${
                        p?.category === "premium"
                          ? "border-[var(--cat-pr-border)] text-[var(--cat-pr-text)] bg-[var(--cat-pr-bg)]/40"
                          : "border-[var(--cat-me-border)] text-[var(--cat-me-text)] bg-[var(--cat-me-bg)]/40"
                      }`}
                    >
                      {p ? `${p.brand} — ${p.name}` : pid.slice(0, 8)}
                      <button onClick={() => togglePick(pid)} className="hover:text-red-400">
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="max-h-56 overflow-y-auto border border-[var(--border-default)]">
              {filteredPickOptions.map((p) => {
                const selected = form.product_ids.includes(p.id);
                return (
                  <button
                    key={p.id}
                    data-testid={`pick-toggle-${p.id}`}
                    onClick={() => togglePick(p.id)}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 border-b border-[var(--border-default)]/50 last:border-b-0 transition-colors ${
                      selected
                        ? "bg-[var(--brand-gold)]/15 text-[var(--brand-gold)]"
                        : "hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)]"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-sm border ${selected ? "bg-[var(--brand-gold)] border-[var(--brand-gold)]" : "border-[var(--border-default)]"}`} />
                    <span className={`text-[10px] uppercase tracking-wider ${p.category === "premium" ? "text-[var(--cat-pr-text)]" : "text-[var(--cat-me-text)]"}`}>
                      {p.category === "premium" ? "P" : "M"}
                    </span>
                    <span className="text-[var(--text-muted)]">{p.brand}</span>
                    <span>{p.name}</span>
                  </button>
                );
              })}
              {filteredPickOptions.length === 0 && (
                <div className="px-3 py-3 text-xs text-[var(--text-muted)]">No perfumes match.</div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button data-testid="combo-save" onClick={save} className="flex items-center gap-1 px-5 py-2 bg-[var(--brand-gold)] text-black text-sm font-medium hover:bg-[var(--brand-gold-hover)]">
              <Save size={14} />
              Save
            </button>
            <button data-testid="combo-cancel" onClick={cancel} className="flex items-center gap-1 px-5 py-2 border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--border-hover)]">
              <X size={14} />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-[var(--border-default)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-default)]">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
              <th className="p-3 w-12">#</th>
              <th className="p-3 w-10"></th>
              <th className="p-3">Title</th>
              <th className="p-3">Occasion / Season</th>
              <th className="p-3">Picks</th>
              <th className="p-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-[var(--text-muted)]">Loading…</td></tr>
            ) : combos.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-[var(--text-muted)]">No combos yet.</td></tr>
            ) : (
              combos.map((c) => (
                <tr key={c.id} data-testid={`combo-row-${c.id}`} className="border-t border-[var(--border-default)]">
                  <td className="p-3 text-[var(--text-muted)]">{c.sort_order}</td>
                  <td className="p-3 text-xl">{c.emoji}</td>
                  <td className="p-3 font-headings">{c.title}</td>
                  <td className="p-3 text-xs text-[var(--text-secondary)]">{c.occasion} · {c.season}</td>
                  <td className="p-3 text-xs text-[var(--text-secondary)]">{(c.product_ids || []).length} picks</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`combo-edit-${c.id}`} onClick={() => startEdit(c)} className="p-2 border border-[var(--border-default)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)]">
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`combo-delete-${c.id}`} onClick={() => del(c)} className="p-2 border border-[var(--border-default)] hover:border-red-500/60 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
